import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { StructureContext } from './structureContextStore'
import { useTenant } from './useTenant'
import { useStructures } from '../hooks/useStructures'

const STORAGE_PREFIX = 'vaultbi.structureFilter.'

function loadFromStorage(schemaName: string): number[] | null {
  if (!schemaName) return null
  try {
    const stored = localStorage.getItem(STORAGE_PREFIX + schemaName)
    if (!stored) return null
    const parsed = JSON.parse(stored) as unknown
    if (!Array.isArray(parsed)) return null
    if (!parsed.every((v) => typeof v === 'number' && Number.isInteger(v))) return null
    return parsed as number[]
  } catch {
    return null
  }
}

function saveToStorage(schemaName: string, ids: number[] | null): void {
  if (!schemaName) return
  try {
    if (ids === null) {
      localStorage.removeItem(STORAGE_PREFIX + schemaName)
    } else {
      localStorage.setItem(STORAGE_PREFIX + schemaName, JSON.stringify(ids))
    }
  } catch {
    // quota exceeded or permission denied — state update still proceeds
  }
}

export function StructureProvider({ children }: { children: ReactNode }) {
  const { selectedTenant } = useTenant()
  const schemaName = selectedTenant?.schemaName ?? ''

  const { structures, isLoading } = useStructures(schemaName)

  const [selectedIds, setSelectedIdsState] = useState<number[] | null>(
    () => loadFromStorage(schemaName),
  )
  const prevSchemaRef = useRef(schemaName)

  useEffect(() => {
    if (prevSchemaRef.current !== schemaName) {
      prevSchemaRef.current = schemaName
      queueMicrotask(() => setSelectedIdsState(loadFromStorage(schemaName)))
    }
  }, [schemaName])

  const normalizeIds = (ids: number[] | null) =>
    ids === null ? null : [...new Set(ids)].sort((a, b) => a - b)

  const setSelectedIds = useCallback(
    (ids: number[] | null) => {
      const normalized = normalizeIds(ids)
      setSelectedIdsState(normalized)
      saveToStorage(schemaName, normalized)
    },
    [schemaName],
  )

  const value = useMemo(
    () => ({ structures, selectedIds, setSelectedIds, isLoading }),
    [structures, selectedIds, setSelectedIds, isLoading],
  )

  return <StructureContext.Provider value={value}>{children}</StructureContext.Provider>
}
